import React from 'react';
import type { TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';

type ElementPropsWithDisabled = {
  disabled?: unknown;
  fullWidth?: unknown;
};

/**
 * Shrink-wrap the trigger so the span does not become a flex/grid item with a
 * different size than the child. `vertical-align` only applies when the parent
 * is inline layout; flex parents ignore it.
 */
export const disabledTooltipInlineWrapperStyle: React.CSSProperties = {
  display: 'inline-flex',
  boxSizing: 'border-box',
  width: 'fit-content',
  height: 'fit-content',
  maxWidth: '100%',
  verticalAlign: 'middle',
};

/**
 * Match a `fullWidth` child: the wrapper is the percentage box, the button fills it.
 */
export const disabledTooltipFullWidthWrapperStyle: React.CSSProperties = {
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
};

/**
 * Pick a wrapper box that occupies the same space the child would have occupied.
 */
const getDisabledTooltipWrapperStyle = (
  child: React.ReactElement<ElementPropsWithDisabled>,
): React.CSSProperties => {
  if (child.props.fullWidth) {
    return disabledTooltipFullWidthWrapperStyle;
  }
  return disabledTooltipInlineWrapperStyle;
};

/**
 * Wrap the trigger so a disabled control can still open the tooltip.
 * Always wrap when `showWhenDisabled` is true so enabling/disabling does not
 * insert or remove a DOM box (layout would jump).
 * @see https://mui.com/material-ui/react-tooltip/#disabled-elements
 */
export const wrapDisabledTooltipChild = (
  child: MuiTooltipProps['children'],
  showWhenDisabled = false,
): MuiTooltipProps['children'] => {
  if (!showWhenDisabled) return child;
  if (!React.isValidElement<ElementPropsWithDisabled>(child)) return child;
  return React.createElement(
    'span',
    { style: getDisabledTooltipWrapperStyle(child) },
    child,
  );
};
